import { z } from "zod";
import { isValidKvnr, normalizeKvnr } from "./kvnr.js";

export interface BirthDate {
  year: number;
  month: number;
  day: number;
  /** Alter in vollen Jahren zum Stichtag */
  age: number;
}

/** Geburtsdatum `TT.MM.JJJJ` oder ISO `JJJJ-MM-TT`; gültig, nicht in der Zukunft, Alter ≤ 120. */
export function parseBirthDate(input: string, now: Date = new Date()): BirthDate | null {
  const s = input.trim();
  const de = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  let day: number, month: number, year: number;
  if (de) {
    day = Number(de[1]);
    month = Number(de[2]);
    year = Number(de[3]);
  } else if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (dt.getTime() > today) return null;
  let age = now.getUTCFullYear() - year;
  if (now.getUTCMonth() + 1 < month || (now.getUTCMonth() + 1 === month && now.getUTCDate() < day)) age--;
  if (age > 120) return null;
  return { year, month, day, age };
}

export const isValidPlz = (plz: string): boolean => /^\d{5}$/.test(plz.trim());
export const isValidIk = (ik: string): boolean => /^\d{9}$/.test(ik.replace(/\s+/g, ""));

/** Anspruchsvoraussetzungen nach § 2 der Vereinbarung (Texte im i18n-Katalog). */
export const ELIGIBILITY_REASONS = ["a", "b", "c", "d"] as const;
export type EligibilityReason = (typeof ELIGIBILITY_REASONS)[number];

export const PATIENT_CAPTURE_SOURCES = ["vision", "lokal_ocr", "manuell"] as const;

const text = (max: number) => z.string().trim().max(max);

/** Stammdaten wie im Prototyp (sanitizePatient), mit Längenbegrenzung. */
export const patientSchema = z.object({
  vorname: text(120),
  name: text(120),
  strasse: text(120),
  plz: text(10),
  ort: text(120),
  kvnr: text(12).transform(normalizeKvnr),
  krankenkasse: text(120),
  ik: text(12).transform((v) => v.replace(/\s+/g, "")),
  geburtsdatum: text(10),
  telefon: text(40),
});
export type PatientData = z.infer<typeof patientSchema>;

export const patientIntakeSchema = z.object({
  patient: patientSchema,
  eligibility: z.array(z.enum(ELIGIBILITY_REASONS)).max(4),
  consent: z.boolean(),
});
export type PatientIntake = z.infer<typeof patientIntakeSchema>;

export type PatientFieldError =
  "required" | "plz_format" | "kvnr_invalid" | "birthdate_invalid" | "ik_format";

export interface PatientValidation {
  fields: Partial<Record<keyof PatientData, PatientFieldError>>;
  eligibilityMissing: boolean;
  consentMissing: boolean;
  valid: boolean;
}

/** Validierung wie im Prototyp (index.html:446-460), mit Fehlercodes statt Texten. */
export function validatePatientIntake(intake: PatientIntake, now: Date = new Date()): PatientValidation {
  const p = intake.patient;
  const fields: PatientValidation["fields"] = {};
  for (const f of ["vorname", "name", "strasse", "ort", "krankenkasse"] as const) {
    if (!p[f].trim()) fields[f] = "required";
  }
  if (!isValidPlz(p.plz)) fields.plz = "plz_format";
  if (!isValidKvnr(p.kvnr)) fields.kvnr = "kvnr_invalid";
  if (!parseBirthDate(p.geburtsdatum, now)) fields.geburtsdatum = "birthdate_invalid";
  if (p.ik.trim() && !isValidIk(p.ik)) fields.ik = "ik_format";
  const eligibilityMissing = intake.eligibility.length === 0;
  const consentMissing = !intake.consent;
  return {
    fields,
    eligibilityMissing,
    consentMissing,
    valid: Object.keys(fields).length === 0 && !eligibilityMissing && !consentMissing,
  };
}

/** Bestätigung (Fail-Safe): wiederholte KVNR muss exakt übereinstimmen und der Abgleich bestätigt sein. */
export function confirmPatient(p: PatientData, kvnrRepeat: string, confirmed: boolean) {
  if (normalizeKvnr(kvnrRepeat) !== normalizeKvnr(p.kvnr))
    return { ok: false as const, error: "kvnr_mismatch" };
  if (!confirmed) return { ok: false as const, error: "confirmation_required" };
  return { ok: true as const };
}

/** Anonymes Warteschlangen-Label: Initialen + Alter (Prototyp: labelFromPatient/patientQueueLabel). */
export function queueLabel(p: Pick<PatientData, "vorname" | "name" | "geburtsdatum">, now = new Date()) {
  const i = p.vorname.trim()[0]?.toUpperCase() ?? "";
  const j = p.name.trim()[0]?.toUpperCase() ?? "";
  const initials = `${i ? i + "." : ""} ${j ? j + "." : ""}`.trim();
  const dob = parseBirthDate(p.geburtsdatum, now);
  return { initials, age: dob ? dob.age : null };
}
